/**
 * Builds OpenSearch nested aggregation queries for GROUP BY mode.
 *
 * Structure: nested terms aggs (one per group-by field) with metric sub-aggs
 * on the innermost level for each non-group-by column.
 *
 * Example output for GROUP BY [company, status] with SUM(amount), COUNT(name):
 * {
 *   "group_by_company": {
 *     "terms": { "field": "company", "size": 500 },
 *     "aggs": {
 *       "group_by_status": {
 *         "terms": { "field": "status", "size": 500 },
 *         "aggs": {
 *           "agg_amount": { "sum": { "field": "amount" } },
 *           "agg_name": { "value_count": { "field": "name" } }
 *         }
 *       }
 *     }
 *   }
 * }
 */

import type { AggregationType, ColumnAggregationMap, DateGroupingMap } from './aggregation-types';
import type { FieldOption } from './opensearch-field-mapper';

/** Max buckets per terms aggregation level */
const TERMS_SIZE = 500;

type SortDirection = 'asc' | 'desc';

interface AggregationSort {
  field: string;
  direction: SortDirection;
}

/**
 * Convert camelCase field key to snake_case for OpenSearch.
 * The /fields endpoint returns camelCase names but OpenSearch indexes use snake_case.
 */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/** Map our AggregationType to OpenSearch aggregation DSL type */
function aggTypeToOsDsl(aggType: AggregationType): string {
  switch (aggType) {
    case 'count':
      return 'value_count';
    case 'sum':
      return 'sum';
    case 'avg':
      return 'avg';
    case 'min':
      return 'min';
    case 'max':
      return 'max';
    case 'cardinality':
      return 'cardinality';
    default:
      return 'value_count';
  }
}

/**
 * Resolve the OpenSearch field path safe to use inside a metric aggregation.
 *
 * Aggregations require doc_values (or fielddata), so analyzed `text` fields
 * cannot be aggregated directly — OpenSearch returns
 * "Text fields are not optimised for operations that require per-document field data"
 * and the entire shard request fails.
 *
 * Reuses the canonical doc-values-safe path that `field-filter-pipeline`
 * already derives onto `FieldOption.sortPath` (same path used for sorting in
 * `preview-table.gts`). For text fields this is typically the `.keyword`
 * subfield; for numeric/date/keyword fields it's the raw path. Falls back to
 * `path` then `camelToSnake(key)` for legacy callers without `sortPath`.
 */
function aggFieldFor(col: FieldOption): string {
  return col.sortPath ?? col.path ?? camelToSnake(col.key);
}

function parseSort(sortField: string | undefined): AggregationSort | undefined {
  if (!sortField) return undefined;

  const field = sortField.replace(/:(?:asc|desc)$/, '');
  const direction = sortField.endsWith(':desc') ? 'desc' : 'asc';

  return { field, direction };
}

function sortMatchesColumn(sortField: string, col: FieldOption) {
  return [col.sortPath, col.path, col.key, col.resultKey].includes(sortField);
}

function sortColumnFor(sort: AggregationSort | undefined, columns: FieldOption[]) {
  if (!sort) return undefined;
  return columns.find((col) => sortMatchesColumn(sort.field, col));
}

function orderForGroupByField(
  fieldKey: string,
  sort: AggregationSort | undefined,
  sortColumn: FieldOption | undefined
) {
  if (!sort) return undefined;

  if (
    sortColumn?.key === fieldKey ||
    sort.field === fieldKey ||
    sort.field === camelToSnake(fieldKey)
  ) {
    return { _key: sort.direction };
  }

  return undefined;
}

function orderForMetricField(
  isInnermost: boolean,
  groupByKeys: string[],
  sort: AggregationSort | undefined,
  sortColumn: FieldOption | undefined
) {
  if (!isInnermost || !sort || !sortColumn || groupByKeys.includes(sortColumn.key)) {
    return undefined;
  }
  return { [`agg_${sortColumn.key}`]: sort.direction };
}

/**
 * Build metric sub-aggregations for the innermost level.
 * One sub-agg per non-group-by column.
 */
function buildMetricAggs(
  columns: FieldOption[],
  groupByKeys: string[],
  columnAggregations: ColumnAggregationMap
): Record<string, unknown> {
  const aggs: Record<string, unknown> = {};

  for (const col of columns) {
    // Skip group-by dimension fields — they appear as bucket keys
    if (groupByKeys.includes(col.key)) continue;

    const aggType = columnAggregations[col.key] || 'count';
    const osDslType = aggTypeToOsDsl(aggType);

    aggs[`agg_${col.key}`] = {
      [osDslType]: { field: aggFieldFor(col) },
    };
  }

  return aggs;
}

/**
 * Build the nested aggregation structure.
 *
 * Uses `date_histogram` for date fields that have a grouping interval,
 * and `terms` for all other fields.
 *
 * @param groupByKeys - ordered array of field keys to group by
 * @param columns - all selected columns (including group-by fields)
 * @param columnAggregations - map of columnKey → AggregationType
 * @param sortField - optional sort field string (e.g., "field:asc")
 * @param dateGroupingIntervals - map of fieldKey → DateGroupingInterval for date fields
 * @returns OpenSearch aggregations object to merge into the query body
 */
export function buildAggregationQuery(
  groupByKeys: string[],
  columns: FieldOption[],
  columnAggregations: ColumnAggregationMap,
  sortField?: string,
  dateGroupingIntervals?: DateGroupingMap
): Record<string, unknown> {
  if (groupByKeys.length === 0) return {};

  const metricAggs = buildMetricAggs(columns, groupByKeys, columnAggregations);
  const sort = parseSort(sortField);
  const sortColumn = sortColumnFor(sort, columns);

  // Build from innermost → outermost
  let currentAggs: Record<string, unknown> = metricAggs;

  for (let i = groupByKeys.length - 1; i >= 0; i--) {
    const fieldKey = groupByKeys[i]!;
    const aggName = `group_by_${fieldKey}`;
    const explicitInterval = dateGroupingIntervals?.[fieldKey];
    // Auto-detect date fields from columns and default to 'year' if no explicit interval
    const fieldColumn = columns.find((c) => c.key === fieldKey);
    const isDateField =
      fieldColumn?.isDate || fieldColumn?.type === 'date' || fieldColumn?.reportDatatype === 'date';
    const dateInterval = explicitInterval || (isDateField ? 'year' : undefined);

    let fieldAgg: Record<string, unknown>;

    if (dateInterval) {
      // Use date_histogram for date fields with a grouping interval
      const order =
        orderForGroupByField(fieldKey, sort, sortColumn) ??
        orderForMetricField(i === groupByKeys.length - 1, groupByKeys, sort, sortColumn);

      fieldAgg = {
        date_histogram: {
          field: camelToSnake(fieldKey),
          calendar_interval: dateInterval,
          format: dateHistogramFormat(dateInterval),
          min_doc_count: 1,
          ...(order ? { order } : {}),
        },
      };
    } else {
      // Standard terms aggregation
      const order =
        orderForGroupByField(fieldKey, sort, sortColumn) ??
        orderForMetricField(i === groupByKeys.length - 1, groupByKeys, sort, sortColumn);

      fieldAgg = {
        terms: { field: camelToSnake(fieldKey), size: TERMS_SIZE, ...(order ? { order } : {}) },
      };
    }

    // Only add sub-aggs if there are any
    if (Object.keys(currentAggs).length > 0) {
      fieldAgg['aggs'] = currentAggs;
    }

    currentAggs = { [aggName]: fieldAgg };
  }

  return currentAggs;
}

/**
 * Get the OpenSearch date format string for a date_histogram interval.
 * Controls what `key_as_string` returns in each bucket.
 */
function dateHistogramFormat(interval: string): string {
  switch (interval) {
    case 'year':
      return 'yyyy';
    case 'quarter':
      return 'yyyy-QQQ';
    case 'month':
      return 'yyyy-MM';
    case 'week':
      return 'yyyy-MM-dd';
    case 'day':
      return 'yyyy-MM-dd';
    default:
      return 'yyyy-MM-dd';
  }
}
